import { LoginCallback } from '@okta/okta-react';
import { createBrowserRouter } from 'react-router-dom';
import AppPublic from '../App-public';
import { ErrorLoginCallbackComponent } from '../components/error-login-callback/error-login-callback.component';
import { FILTER_NAMES } from '../components/filters/hooks/filter-names.constants';
import { FilterDecodeProvider } from '../components/filters/provider/filter-decode-provider.component';
import { FilterProvider } from '../components/filters/provider/filter-provider.component';
import { FilterStandAloneDecodeProvider } from '../components/filters/provider/filter-stand-alone-decode-provider.component';
import { MessagePageComponent } from '../components/message-page/message-page.component';
import { MODULE_ID } from '../components/reports/constants/module-id.constants';
import RequireAuth from '../components/requireAuth/requireAuth';
import DecompPageComponent from './decomp/decomp-page.component';
import { DecompPageFilterProvider } from './decomp/providers/filters/decomp-page-filter-provider.component';
import { DecompInsightsFilterProvider } from './decomp-insights/providers/filters/decomp-insights-filter-provider.component';
import DemandPageComponent from './demand/demand-page.component';
import { DemandPageFilterProvider } from './demand/providers/filters/demand-page-filter-provider.component';
import {
  UnderConstructionModule,
  OktaSecurityModule,
  AppModule,
  LayoutWideModule,
  LayoutSidePanelsModule,
  HomeModule,
  AboutModule,
  RecentVisitModule,
  DownloadPageModule,
  TrainingAndResourcesModule,
  IrisHelpCenterModule,
  SendEmailPageModule,
  DecompInsightsPageModule,
  ReleasesModules,
  AlertModule,
  CreateAlertModule,
  AlertsPageModule,
  NetFlowModule,
  ScenarioPlannerLandingPageModule,
  ScenarioPlannerComparePageModule,
  SimpleLayoutModule,
  NetflowLayoutModule,
} from './modules';
import { NetflowFilterProvider } from './netflow/providers/filters/netflow-filter-provider.component';
import TrainingVideoComponent from './recentvisits/components/trainingvideo.component';
import ScenarioPlannerDetailsPage from './scenario-planner/scenario-planner-details-page.component';

const DECOMP_MODEL_CHIPS = [
  FILTER_NAMES.CATEGORY_DRILLTHROUGH,
  FILTER_NAMES.BRAND_DRILLTHROUGH,
  FILTER_NAMES.SUBBRAND_DRILLTHROUGH,
  FILTER_NAMES.SIZEGROUP_DRILLTHROUGH,
  FILTER_NAMES.PPG_DRILLTHROUGH,
  FILTER_NAMES.SIZEGROUP,
  FILTER_NAMES.PPG,
  FILTER_NAMES.SUBPPG,
  FILTER_NAMES.UPC,
];
const DECOMP_INSIGHTS_CHIPS = DECOMP_MODEL_CHIPS.concat([
  FILTER_NAMES.MARKETMULTI,
]);

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppPublic />,
    children: [
      {
        path: '',
        element: <LayoutWideModule />,
        children: [
          {
            path: '',
            index: true,
            element: <HomeModule />,
          },
          {
            path: 'capabilities',
            element: <RecentVisitModule />,
          },
          {
            path: 'training/:trainingAndResourcesSection?',
            element: <TrainingAndResourcesModule />,
          },
          {
            path: 'resources',
            element: <UnderConstructionModule />,
          },
          {
            path: 'about',
            element: <AboutModule />,
          },
          {
            path: 'help-center/:helpCenterSection?',
            element: <IrisHelpCenterModule />,
          },
          {
            path: 'feedback',
            element: <UnderConstructionModule />,
          },
          {
            path: 'releases/:releaseNote',
            element: <ReleasesModules />,
          },
          {
            path: 'f',
            element: <CreateAlertModule />,
          },
          {
            path: 'video/:title',
            element: <TrainingVideoComponent />,
          },
          {
            path: 'alerts',
            element: <AlertModule />,
          },
        ],
      },
    ],
  },
  {
    path: '',
    element: <OktaSecurityModule />,
    children: [
      {
        path: 'login/callback',
        element: (
          <LoginCallback
            errorComponent={ErrorLoginCallbackComponent}
            loadingElement={
              <MessagePageComponent>
                <p>Login...</p>
              </MessagePageComponent>
            }
          />
        ),
      },
      {
        path: 't',
        element: <AppModule />,
        children: [
          // Demand model
          {
            path: 'r',
            element: (
              <FilterDecodeProvider
                FilterSelectionReceptorComponent={FilterProvider}
                moduleId={MODULE_ID.DEFAULT}
              >
                <DemandPageFilterProvider>
                  <LayoutSidePanelsModule className="demand-model" />
                </DemandPageFilterProvider>
              </FilterDecodeProvider>
            ),
            children: [
              {
                path: '',
                element: <DemandPageComponent />,
              },
            ],
          },
          // Drill through (decomp model)
          {
            path: 'r/:d',
            element: (
              <FilterDecodeProvider
                FilterSelectionReceptorComponent={FilterProvider}
                moduleId={MODULE_ID.DEFAULT}
              >
                <DecompPageFilterProvider>
                  <LayoutSidePanelsModule
                    chipsFilterId={DECOMP_MODEL_CHIPS}
                    className="decomp-model"
                  />
                </DecompPageFilterProvider>
              </FilterDecodeProvider>
            ),
            children: [
              {
                path: '',
                element: <DecompPageComponent />,
              },
            ],
          },
          // Decomp Insights
          {
            path: 'i',
            element: (
              <FilterDecodeProvider
                FilterSelectionReceptorComponent={FilterProvider}
                moduleId={MODULE_ID.DECOMP_INSIGHTS}
              >
                <DecompInsightsFilterProvider>
                  <LayoutSidePanelsModule
                    leftSideFilterId={[
                      FILTER_NAMES.METACATEGORY,
                      FILTER_NAMES.MARKETMULTI,
                    ]}
                    chipsFilterId={DECOMP_INSIGHTS_CHIPS}
                    className="decomp-insights"
                  />
                </DecompInsightsFilterProvider>
              </FilterDecodeProvider>
            ),
            children: [
              {
                path: '',
                element: <DecompInsightsPageModule />,
              },
            ],
          },
          //netflow
          {
            path: 'n',
            element: (
              <FilterDecodeProvider
                FilterSelectionReceptorComponent={FilterProvider}
                moduleId={MODULE_ID.NETFLOW}
              >
                <NetflowFilterProvider>
                  <NetflowLayoutModule />
                </NetflowFilterProvider>
              </FilterDecodeProvider>
            ),
            children: [
              {
                path: '',
                element: <NetFlowModule />,
              },
            ],
          },
          // Alerts
          {
            path: 'a',
            element: (
              <FilterDecodeProvider
                FilterSelectionReceptorComponent={FilterProvider}
                moduleId={MODULE_ID.ALERTS}
              >
                <DecompInsightsFilterProvider>
                  <LayoutSidePanelsModule className="alerts" />
                </DecompInsightsFilterProvider>
              </FilterDecodeProvider>
            ),
            children: [
              {
                path: '',
                element: <AlertsPageModule />,
              },
            ],
          },
          // Scenario Planner
          {
            path: 's',
            element: (
              <RequireAuth navigationId="scenario-planner">
                <FilterDecodeProvider
                  FilterSelectionReceptorComponent={FilterProvider}
                  moduleId={MODULE_ID.SCENARIO_PLANNER}
                >
                  <SimpleLayoutModule className="scenario-planner" />
                </FilterDecodeProvider>
              </RequireAuth>
            ),
            children: [
              {
                path: '',
                element: <ScenarioPlannerLandingPageModule />,
              },
              {
                path: 'c/:ids',
                element: <ScenarioPlannerComparePageModule />,
              },
            ],
          },
          {
            path: 's/:id',
            element: (
              <FilterStandAloneDecodeProvider
                FilterSelectionReceptorComponent={FilterProvider}
                moduleId={MODULE_ID.SCENARIO_PLANNER_DETAILS}
              >
                <SimpleLayoutModule className="scenario-planner-datails" />
              </FilterStandAloneDecodeProvider>
            ),
            children: [
              {
                path: '',
                element: <ScenarioPlannerDetailsPage />,
              },
            ],
          },
          // Demand model, download
          {
            path: 'r/download/:report_name/:filename',
            element: (
              <FilterDecodeProvider
                FilterSelectionReceptorComponent={DownloadPageModule}
                moduleId={MODULE_ID.DEFAULT}
              />
            ),
          },
          // Drill through, download
          {
            path: 'r/d/download/:report_name/:filename',
            element: (
              <FilterDecodeProvider
                FilterSelectionReceptorComponent={DownloadPageModule}
                moduleId={MODULE_ID.DEFAULT}
              />
            ),
          },
          // Decomp Insights, download
          {
            path: 'i/download/:report_name/:filename',
            element: (
              <FilterDecodeProvider
                FilterSelectionReceptorComponent={DownloadPageModule}
                moduleId={MODULE_ID.DECOMP_INSIGHTS}
              />
            ),
          },
          // Netflow, download
          {
            path: 'n/download/:report_name/:filename',
            element: (
              <FilterDecodeProvider
                FilterSelectionReceptorComponent={DownloadPageModule}
                moduleId={MODULE_ID.NETFLOW}
              />
            ),
          },
          // Scenario Planner
          {
            path: 's/:report_id/download/:report_name/:filename',
            element: (
              <FilterDecodeProvider
                FilterSelectionReceptorComponent={DownloadPageModule}
                moduleId={MODULE_ID.SCENARIO_PLANNER}
              />
            ),
          },
          {
            path: 'contact-us',
            element: (
              <SendEmailPageModule
                messagePrefix=""
                subjectPrefix="Contact Us"
              />
            ),
          },
          {
            path: 'report-a-problem',
            element: (
              <SendEmailPageModule
                messagePrefix=""
                subjectPrefix="Report a problem"
              />
            ),
          },
        ],
      },
    ],
  },
]);

export default router;
